import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import configService, { 
  Currency, 
  CreateCurrencyDto, 
  UpdateCurrencyDto, 
  CurrencyFilters,
  PaginatedResponse 
} from '@/services/configService'

interface CurrencyState {
  currencies: Currency[]
  currentCurrency: Currency | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  filters: CurrencyFilters

  // Actions
  setFilters: (filters: CurrencyFilters) => void
  fetchCurrencies: (filters?: CurrencyFilters) => Promise<void>
  fetchCurrencyById: (id: string) => Promise<void>
  createCurrency: (data: CreateCurrencyDto) => Promise<Currency>
  updateCurrency: (id: string, data: UpdateCurrencyDto) => Promise<Currency>
  deleteCurrency: (id: string) => Promise<void>
  clearError: () => void
  clearCurrentCurrency: () => void
}

const useCurrencyStore = create<CurrencyState>()(
  devtools(
    (set, get) => ({
      currencies: [],
      currentCurrency: null,
      loading: false,
      error: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      filters: {
        page: 1,
        limit: 10
      },

      setFilters: (filters) => {
        set({ filters: { ...get().filters, ...filters } })
      },

      fetchCurrencies: async (filters = {}) => {
        try {
          set({ loading: true, error: null })
          const currentFilters = { ...get().filters, ...filters }
          const response: PaginatedResponse<Currency> = await configService.currencies.getAll(currentFilters)
          
          set({
            currencies: response.data,
            pagination: {
              page: currentFilters.page || 1,
              limit: currentFilters.limit || 10,
              total: response.total,
              pages: Math.ceil(response.total / (currentFilters.limit || 10))
            },
            filters: currentFilters,
            loading: false
          })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors du chargement des devises',
            loading: false
          })
        }
      },

      fetchCurrencyById: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const currency = await configService.currencies.getById(id)
          set({ currentCurrency: currency, loading: false })
        } catch (error: any) {
          console.error('Error fetching currency by ID:', error)
          const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'Erreur lors du chargement de la devise'
          set({
            error: errorMessage,
            loading: false
          })
        }
      },

      createCurrency: async (data: CreateCurrencyDto) => {
        try {
          set({ loading: true, error: null })
          const currency = await configService.currencies.create(data)
          
          // Add to currencies list
          set((state) => ({
            currencies: [currency, ...state.currencies],
            loading: false
          }))

          return currency
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la création de la devise',
            loading: false
          })
          throw error
        }
      },

      updateCurrency: async (id: string, data: UpdateCurrencyDto) => {
        try {
          set({ loading: true, error: null })
          const currency = await configService.currencies.update(id, data)
          
          // Update in currencies list
          set((state) => ({
            currencies: state.currencies.map(c => 
              c.id === id ? currency : c
            ),
            currentCurrency: state.currentCurrency?.id === id ? currency : state.currentCurrency,
            loading: false
          }))

          return currency
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la mise à jour de la devise',
            loading: false
          })
          throw error
        }
      },

      deleteCurrency: async (id: string) => {
        try {
          set({ loading: true, error: null })
          await configService.currencies.delete(id)
          
          // Remove from currencies list
          set((state) => ({
            currencies: state.currencies.filter(c => c.id !== id),
            currentCurrency: state.currentCurrency?.id === id ? null : state.currentCurrency,
            loading: false
          }))
        } catch (error: any) {
          console.error('Error deleting currency:', error)
          const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'Erreur lors de la suppression de la devise'
          set({
            error: errorMessage,
            loading: false
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),
      clearCurrentCurrency: () => set({ currentCurrency: null })
    }),
    { name: 'CurrencyStore' }
  )
)

export default useCurrencyStore
